// ============================================
// SmartNotificationSystem.js - Self-Validating
// Schedules upfront, validates before sending
// ============================================

import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';

export class SmartNotificationSystem {
  constructor(azureConfig, firestoreService, memoryService) {
    this.azure = azureConfig;
    this.firestore = firestoreService;
    this.memory = memoryService;
    this.platform = Capacitor.getPlatform();
  // ✅ GET USER'S TIMEZONE
  this.userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  console.log(`🌍 User timezone: ${this.userTimezone}`);
}

  // ============================================
  // 1. INIT
  // ============================================
  
  async init() {
    console.log('🔔 Initializing notifications...');
    
    // Request permission
    const permission = await LocalNotifications.requestPermissions();
    if (permission.display !== 'granted') {
      console.warn('⚠️ Permission denied');
      return false;
    }
    
    // Register actions
    await this.registerActions();
    
    // Setup listeners
    this.setupListeners();
    
    console.log('✅ Notifications ready');
    return true;
  }

  // ============================================
  // 2. REGISTER ACTIONS
  // ============================================
  
  async registerActions() {
    if (this.platform === 'web') return;
    
    await LocalNotifications.registerActionTypes({
      types: [{
        id: 'FOLLOW_UP',
        actions: [
          {
            id: 'reply',
            title: 'Reply',
            input: true,
            inputButtonTitle: 'Send',
            inputPlaceholder: 'Type reply...'
          },
          {
            id: 'view',
            title: 'View',
            foreground: true
          }
        ]
      }]
    });
  }

  // ============================================
  // 3. SETUP LISTENERS
  // ============================================
  
  setupListeners() {
    // When notification is about to show
    LocalNotifications.addListener('localNotificationReceived', async (notif) => {
      console.log('🔔 Notification received, validating...');
      
      const memoryId = notif.extra?.memoryId;
      const scheduledId = notif.extra?.scheduledId;
      
      // ✅ VALIDATE: Is this still relevant?
      const isValid = await this.validateNotification(memoryId, scheduledId);
      
      if (!isValid) {
        console.log('❌ Notification no longer valid - cancelling');
        await LocalNotifications.cancel({ notifications: [{ id: notif.id }] });
        return;
      }
      
      console.log('✅ Notification valid - allowing');
    });
    
    // When user interacts
    LocalNotifications.addListener('localNotificationActionPerformed', async (notif) => {
      const { actionId, inputValue, notification } = notif;
      
      if (actionId === 'reply' && inputValue) {
        await this.handleReply(inputValue, notification);
      }
      else if (actionId === 'view') {
        await this.handleView(notification);
      }
    });
  }

  // ============================================
  // 4. SCHEDULE NOTIFICATION (When memory saved)
  // ============================================
  
  async scheduleFollowUp(memory) {
    try {
      console.log('📅 Scheduling follow-up for:', memory.memory.substring(0, 40));
      
      // Extract when event happens
      const eventTime = this.extractEventTime(memory.memory);
      if (!eventTime) {
        console.log('⏭️ No time found, skipping');
        return null;
      }
      
      // Schedule notification for 3 hours after event
      const followUpTime = new Date(eventTime.getTime() + (3 * 60 * 60 * 1000));
      
      // Don't schedule if in the past
      if (followUpTime < new Date()) {
        console.log('⏭️ Follow-up time already passed');
        return null;
      }
      
      console.log(`⏰ Will send at: ${followUpTime.toLocaleString()}`);
      
      // Generate message using LLM
      const message = await this.generateFollowUpMessage(memory.memory);
      
      // Create unique ID for this scheduled notification
      const scheduledId = `notif_${memory.id}_${Date.now()}`;
      
      const config = {
        title: 'Preptune',
        body: message,
        id: Date.now(),
        schedule: { at: followUpTime },
        extra: {
          memoryId: memory.id,
          scheduledId: scheduledId,
          eventText: memory.memory,
          type: 'follow_up',
          scheduledFor: followUpTime.toISOString()
        }
      };
      
      if (this.platform !== 'web') {
        config.actionTypeId = 'FOLLOW_UP';
      }
      
      await LocalNotifications.schedule({
        notifications: [config]
      });
      
      // Save scheduled notification to Firestore
      await this.firestore.saveScheduledNotification({
        scheduledId: scheduledId,
        memoryId: memory.id,
        message: message,
        scheduledFor: followUpTime.toISOString(),
        eventText: memory.memory,
        status: 'scheduled',
        createdAt: new Date().toISOString()
      });
      
      console.log(`✅ Scheduled for ${followUpTime.toLocaleString()}`);
      return scheduledId;
      
    } catch (error) {
      console.error('❌ Schedule failed:', error);
      return null;
    }
  }

  // ============================================
  // 5. VALIDATE NOTIFICATION (Before sending)
  // ============================================
  
  async validateNotification(memoryId, scheduledId) {
    try {
      console.log('🔍 Validating notification...');
      
      // Get scheduled notification from Firestore
      const scheduled = await this.firestore.getScheduledNotification(scheduledId);
      
      if (!scheduled) {
        console.log('❌ Not found in Firestore');
        return false;
      }
      
      // Check if already cancelled
      if (scheduled.status === 'cancelled') {
        console.log('❌ Already cancelled');
        return false;
      }
      
      // Get recent chat messages (last 20)
      const recentMessages = await this.firestore.getRecentChatMessages(20);
      
      if (!recentMessages || recentMessages.length === 0) {
        console.log('✅ No recent chat, notification is valid');
        return true;
      }
      
      // ✅ ASK LLM: Did user already talk about this?
      const isStillRelevant = await this.checkRelevance(
        scheduled.eventText,
        recentMessages
      );
      
      if (!isStillRelevant) {
        console.log('❌ User already discussed this event');
        
        // Mark as cancelled in Firestore
        await this.firestore.updateScheduledNotification(scheduledId, {
          status: 'cancelled',
          cancelledAt: new Date().toISOString(),
          reason: 'already_discussed'
        });
        
        return false;
      }
      
      console.log('✅ Still relevant');
      return true;
      
    } catch (error) {
      console.error('❌ Validation error:', error);
      // On error, allow notification (fail safe)
      return true;
    }
  }

  // ============================================
  // 6. CHECK RELEVANCE (LLM validation)
  // ============================================
  
  async checkRelevance(eventText, recentMessages) {
    try {
      // Build conversation context
      const chatContext = recentMessages
        .map(m => `${m.type === 'user' ? 'User' : 'Bot'}: ${m.content}`)
        .join('\n');
      
      const response = await fetch(
        `${this.azure.endpoint}/openai/deployments/${this.azure.deployment}/chat/completions?api-version=2024-02-15-preview`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'api-key': this.azure.apiKey
          },
          body: JSON.stringify({
            messages: [
              {
                role: 'system',
                content: `You're checking if a follow-up notification is still relevant.

SCHEDULED EVENT: "${eventText}"

RECENT CHAT (last 20 messages):
${chatContext}

TASK: Did the user already talk about this event?

Return ONLY JSON:
{
  "alreadyDiscussed": true/false,
  "reason": "brief explanation",
  "relevantMessages": ["quote if found"]
}

RULES:
- alreadyDiscussed = true if user mentioned the event happened/how it went
- alreadyDiscussed = true if user said "it was great/bad/ok"
- alreadyDiscussed = false if event not mentioned at all
- alreadyDiscussed = false if only mentioned as upcoming (not past)`
              }
            ],
            temperature: 0.3,
            max_tokens: 150,
            response_format: { type: 'json_object' }
          })
        }
      );
      
      if (!response.ok) {
        console.warn('⚠️ LLM check failed, assuming relevant');
        return true;
      }
      
      const data = await response.json();
      const result = JSON.parse(data.choices[0].message.content);
      
      console.log('🧠 LLM says:', result.alreadyDiscussed ? '❌ Already discussed' : '✅ Still relevant');
      console.log('   Reason:', result.reason);
      
      // Return opposite (if already discussed, NOT relevant)
      return !result.alreadyDiscussed;
      
    } catch (error) {
      console.error('❌ Relevance check failed:', error);
      return true; // Fail safe: assume relevant
    }
  }

  // ============================================
  // 7. EXTRACT EVENT TIME
  // ============================================
  
  extractEventTime(text) {
    const lower = text.toLowerCase();
  // ✅ Create date in USER'S timezone
  const now = new Date();
  const eventDate = new Date(now.toLocaleString('en-US', { 
    timeZone: this.userTimezone 
  }));
  
    // Tomorrow
    if (/tomorrow/i.test(lower)) {
      eventDate.setDate(eventDate.getDate() + 1);
    }
    // Today
    else if (/today/i.test(lower)) {
      // Keep today
    }
    // Tonight
    else if (/tonight/i.test(lower)) {
      eventDate.setHours(20, 0, 0, 0);
      return eventDate;
    }
    // Day of week
    else {
      const dayMatch = lower.match(/\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/i);
      if (dayMatch) {
        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const targetDay = days.indexOf(dayMatch[1].toLowerCase());
        const currentDay = eventDate.getDay();
        
        let daysToAdd = targetDay - currentDay;
        if (daysToAdd <= 0) daysToAdd += 7;
        
        eventDate.setDate(eventDate.getDate() + daysToAdd);
      } else {
        // No day reference found
        return null;
      }
    }
    
    // Extract time (5pm, 3:30pm, etc)
    const timeMatch = lower.match(/(\d{1,2}):?(\d{2})?\s*(am|pm)?/i);
    if (timeMatch) {
      let hours = parseInt(timeMatch[1]);
      const mins = timeMatch[2] ? parseInt(timeMatch[2]) : 0;
      const period = timeMatch[3];
      
      if (period && period.toLowerCase() === 'pm' && hours !== 12) {
        hours += 12;
      } else if (period && period.toLowerCase() === 'am' && hours === 12) {
        hours = 0;
      }
      
      eventDate.setHours(hours, mins, 0, 0);
    } else {
      // Default to 6pm if no time specified
      eventDate.setHours(18, 0, 0, 0);
    }
    
    return eventDate;
  }

  // ============================================
  // 8. GENERATE FOLLOW-UP MESSAGE (LLM)
  // ============================================
  
  async generateFollowUpMessage(eventText) {
    try {
      const userName = await this.getUserName();
      
      const response = await fetch(
        `${this.azure.endpoint}/openai/deployments/${this.azure.deployment}/chat/completions?api-version=2024-02-15-preview`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'api-key': this.azure.apiKey
          },
          body: JSON.stringify({
            messages: [
              {
                role: 'system',
                content: `Generate a casual follow-up message for ${userName || 'the user'}.

EVENT: "${eventText}"

TASK: Create ONE sentence asking how it went.

RULES:
- Sound like a real friend texting
- Be specific (use event name)
- Examples: "Hey how'd that game go?" / "How was the exam?" / "How'd it turn out?"

Respond ONLY with the message (no quotes, no explanation).`
              }
            ],
            temperature: 0.8,
            max_tokens: 30
          })
        }
      );
      
      if (!response.ok) {
        return "Hey how'd that go?";
      }
      
      const data = await response.json();
      return data.choices[0].message.content.trim();
      
    } catch (error) {
      console.error('❌ Message generation failed:', error);
      return "Hey how'd that go?";
    }
  }

  // ============================================
  // 9. HANDLE REPLY
  // ============================================
  
  async handleReply(userReply, notification) {
    console.log('💬 Reply:', userReply);
    
    try {
      const eventText = notification.extra?.eventText;
      const memoryId = notification.extra?.memoryId;
      const scheduledId = notification.extra?.scheduledId;
      
      // Save to Firestore
      const replyDoc = await this.firestore.saveQuickReply({
        userReply: userReply,
        eventText: eventText,
        memoryId: memoryId,
        scheduledId: scheduledId,
        timestamp: new Date().toISOString()
      });
      
      // Generate AI response
      const aiResponse = await this.generateAIResponse(userReply, eventText);
      
      // Send AI response as notification
      await LocalNotifications.schedule({
        notifications: [{
          title: 'Preptune',
          body: aiResponse,
          id: Date.now(),
          schedule: { at: new Date(Date.now() + 2000) },
          extra: {
            type: 'ai_response',
            replyDocId: replyDoc.id,
            originalReply: userReply
          }
        }]
      });
      
      // Update Firestore
      await this.firestore.updateQuickReply(replyDoc.id, {
        aiResponse: aiResponse,
        respondedAt: new Date().toISOString()
      });
      
      // Mark scheduled notification as completed
      if (scheduledId) {
        await this.firestore.updateScheduledNotification(scheduledId, {
          status: 'replied',
          repliedAt: new Date().toISOString()
        });
      }
      
      // ✅ TRIGGER CHAT LOAD
      window.dispatchEvent(new CustomEvent('notification-reply', {
        detail: {
          userReply: userReply,
          aiResponse: aiResponse,
          eventText: eventText,
          replyDocId: replyDoc.id,
          loadInChat: true
        }
      }));
      
      console.log('✅ Reply handled');
      
    } catch (error) {
      console.error('❌ Reply failed:', error);
    }
  }

  // ============================================
  // 10. HANDLE VIEW
  // ============================================
  
  async handleView(notification) {
    console.log('👀 View in app');
    
    window.dispatchEvent(new CustomEvent('notification-view', {
      detail: {
        eventText: notification.extra?.eventText,
        memoryId: notification.extra?.memoryId,
        openChat: true
      }
    }));
  }

  // ============================================
  // 11. GENERATE AI RESPONSE
  // ============================================
  
  async generateAIResponse(userReply, eventText) {
    try {
      const response = await fetch(
        `${this.azure.endpoint}/openai/deployments/${this.azure.deployment}/chat/completions?api-version=2024-02-15-preview`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'api-key': this.azure.apiKey
          },
          body: JSON.stringify({
            messages: [
              {
                role: 'system',
                content: `You asked about: "${eventText}"
User replied: "${userReply}"

Respond naturally (1-2 sentences). Sound like a real friend.

Examples:
- "That's awesome! Glad to hear it 😊"
- "Ah sorry to hear that. Want to talk about it?"
- "Nice! Sounds like it went well"

Respond ONLY with the message.`
              }
            ],
            temperature: 0.8,
            max_tokens: 60
          })
        }
      );
      
      if (!response.ok) {
        return "Got it! Thanks for letting me know.";
      }
      
      const data = await response.json();
      return data.choices[0].message.content.trim();
      
    } catch (error) {
      console.error('❌ AI response failed:', error);
      return "Got it! Thanks for letting me know.";
    }
  }

  // ============================================
  // 12. CANCEL NOTIFICATION
  // ============================================
  
  async cancelScheduled(scheduledId) {
    try {
      // Mark as cancelled in Firestore
      await this.firestore.updateScheduledNotification(scheduledId, {
        status: 'cancelled',
        cancelledAt: new Date().toISOString()
      });
      
      // Note: Can't cancel already-scheduled Capacitor notifications
      // But validation will catch it before showing
      
      console.log('✅ Notification cancelled:', scheduledId);
      
    } catch (error) {
      console.error('❌ Cancel failed:', error);
    }
  }

  // ============================================
  // 13. GET USER NAME
  // ============================================
  
  async getUserName() {
    try {
      const settings = await this.firestore.getUserSettings();
      return settings?.userName || null;
    } catch (error) {
      return null;
    }
  }

  // ============================================
  // 14. CLEANUP
  // ============================================
  
  cleanup() {
    LocalNotifications.removeAllListeners();
    console.log('✅ Cleaned up');
  }
}

export default SmartNotificationSystem;